import {useCallback, useEffect, useMemo} from "react";
import {useLocation, useSearchParams} from "react-router-dom";
import {rememberListUrl} from "../shared/utils/listMemory";

/**
 * A list's page, search, sorting and status filter, kept in the address
 * (`/orders?status=PA&search=ann&ordering=-number&page=2`) instead of in the
 * page's memory. Opening an order and coming back, a reload, or a shared link
 * then show the same list; the menu link to the plain path starts over.
 *
 * What equals the default is left out of the address. The status filter has
 * three states: absent (the default), `?status=OP&status=CO` (those), and
 * `?status=` (nothing ticked, which means no filter at all).
 *
 * Changes replace the history entry instead of adding one, so Back leaves the
 * list instead of stepping through every filter or keystroke. Changing the
 * search, sorting or statuses goes back to page 1, since the old page number
 * may not exist in the new results.
 *
 * The address is also remembered, for the links that lead back to the list.
 *
 * @template {string} Code
 * @param {{
 *   basePath: string,
 *   defaultOrdering: string,
 *   statusCodes?: Code[],
 *   defaultStatuses?: Code[],
 * }} options `statusCodes` (every status, in display order) and
 *   `defaultStatuses` are only for lists that filter by status
 */
export const useListParams = ({basePath, defaultOrdering, statusCodes, defaultStatuses}) => {
    const [params, setParams] = useSearchParams();
    const location = useLocation();

    useEffect(() => {
        rememberListUrl(basePath, location.pathname + location.search);
    }, [basePath, location.pathname, location.search]);

    const pageParam = Number(params.get("page"));
    const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

    const orderingParam = params.get("ordering");
    // a field name, "-" in front for descending: nothing else goes to the API
    const ordering = orderingParam && /^-?[a-z_]+$/.test(orderingParam) ? orderingParam : defaultOrdering;

    const search = params.get("search") ?? "";

    // A key that changes only when the statuses do, so `statuses` keeps its
    // identity across renders (the lists refetch when it changes).
    const statusKey = params.has("status") ? params.getAll("status").join(",") : null;
    const statuses = useMemo(() => {
        if (!statusCodes) return undefined;
        if (statusKey === null) return defaultStatuses ?? [];
        const wanted = statusKey.split(",");
        return statusCodes.filter((code) => wanted.includes(code));
    }, [statusKey, statusCodes, defaultStatuses]);

    const update = useCallback(
        (/** @type {{page?: number, search?: string, ordering?: string, statuses?: Code[]}} */ changes) => {
            setParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    const set = (/** @type {string} */ key, /** @type {string|null} */ value) =>
                        value === null ? next.delete(key) : next.set(key, value);

                    if ("search" in changes) set("search", changes.search || null);
                    if ("ordering" in changes) {
                        set("ordering", changes.ordering === defaultOrdering ? null : (changes.ordering ?? null));
                    }
                    if ("statuses" in changes) {
                        const chosen = changes.statuses ?? [];
                        next.delete("status");
                        const isDefault =
                            defaultStatuses &&
                            chosen.length === defaultStatuses.length &&
                            chosen.every((code) => defaultStatuses.includes(code));
                        if (!isDefault) {
                            // `?status=` on its own is "nothing ticked"
                            if (chosen.length === 0) next.set("status", "");
                            chosen.forEach((code) => next.append("status", code));
                        }
                    }
                    const newPage = "page" in changes ? changes.page : 1;
                    set("page", newPage && newPage > 1 ? String(newPage) : null);
                    return next;
                },
                {replace: true}
            );
        },
        [setParams, defaultOrdering, defaultStatuses]
    );

    return {
        page,
        search,
        ordering,
        statuses,
        setPage: (/** @type {number} */ next) => update({page: next}),
        setSearch: (/** @type {string} */ next) => update({search: next}),
        setOrdering: (/** @type {string} */ next) => update({ordering: next}),
        setStatuses: (/** @type {Code[]} */ next) => update({statuses: next}),
    };
};
